import { SetStateAction, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from 'components/ui/form';
import { Input } from 'components/ui/input';
import { Button } from 'components/ui/button';
import { CountrySelect } from 'components/ui/country-select';
import {
  getSignupFormValidationSchema,
  signupFormDefaultValue,
  signupFormType,
} from './utils';
import { Captcha } from 'features/captcha';
import { useSignup } from 'features/auth/hooks/use-auth';
import { useNavigate } from 'react-router-dom';
import { FormCheckbox } from 'components/core/uCheckbox/formCheckbox';
import { useTranslation } from 'react-i18next';
import { useConfig } from 'hooks/use-config';
import { trackEvent } from 'analytics/tracker';
import { EVENTS } from 'analytics/events';
import posthog from 'analytics/posthogClient';
import { useTheme } from 'components/core/theme-provider';

/**
 * SignupForm Component
 *
 * A user registration form component that collects user information including name, email, and country,
 * and verifies user interaction via CAPTCHA before allowing form submission. It ensures basic validation
 * using a Zod schema and integrates reCAPTCHA for bot protection.
 *
 * Features:
 * - First name and last name fields with validation
 * - Email field with validation
 * - Searchable country selection dropdown with flag emojis
 * - Real-time country search and filtering
 * - Form validation using Zod and React Hook Form
 * - Google reCAPTCHA integration for bot prevention
 * - CAPTCHA required for enabling form submission
 * - Terms of Service and Privacy Policy acknowledgement checkbox
 * - Submit button disabled until CAPTCHA is verified
 * - Loading state handling during async submission
 *
 * @returns {JSX.Element} The rendered signup form with validation and CAPTCHA security
 *
 * @example
 * // Basic usage
 * <SignupForm />
 *
 * // Within a registration page
 * <div className="auth-container">
 *   <h1>Create Your Account</h1>
 *   <SignupForm />
 *   <div className="auth-footer">
 *     <p>Already have an account? <Link to="/signin">Sign in</Link></p>
 *   </div>
 * </div>
 */

export const SignupForm = () => {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const captchaTheme = theme === 'dark' ? 'dark' : 'light';
  const [captchaToken, setCaptchaToken] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { configurationSettings } = useConfig();
  const navigate = useNavigate();
  const { isPending, mutateAsync } = useSignup();
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  const [resetCaptcha, setResetCaptcha] = useState<() => void>(() => { });

  const form = useForm<signupFormType>({
    defaultValues: signupFormDefaultValue,
    resolver: zodResolver(getSignupFormValidationSchema),
  });

  const { isValid } = form.formState;

  const handleCaptchaVerify = (token: SetStateAction<string>) => {
    setCaptchaToken(token);
  };

  const handleCaptchaExpired = () => {
    setCaptchaToken('');
  };

  const onSubmitHandler = async (values: signupFormType) => {
    if (!captchaToken) {
      return;
    }

    setIsSubmitting(true);
    try {
      const res = await mutateAsync({
        captchaCode: captchaToken,
        ...values,
      });

      if (res.isSuccess) {
        if (res.itemId) {
          posthog.identify(res.itemId);
          trackEvent(EVENTS.USER_SIGNED_UP(res.itemId, {
            firstName: values.firstName,
            lastName: values.lastName
          }));
          posthog.people.set(res.itemId);
        }
        navigate('/signup-email-sent', {
          state: { email: values.email },
        });
      }
    } catch (_error) {
      console.error(_error);
    } finally {
      setCaptchaToken('');
      resetCaptcha();
      setIsSubmitting(false);
    }
  };

  return (
    <div className="w-full">
      <Form {...form}>
        <form className="space-y-4" onSubmit={form.handleSubmit(onSubmitHandler)}>
        <div className="grid grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="firstName"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-sm">{t('FIRST_NAME')}</FormLabel>
                <FormControl>
                  <Input type="text" placeholder={t('ENTER_YOUR_FIRST_NAME')} {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="lastName"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-sm">{t('LAST_NAME')}</FormLabel>
                <FormControl>
                  <Input type="text" placeholder={t('ENTER_YOUR_LAST_NAME')} {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-sm">{t('EMAIL')}</FormLabel>
                <FormControl>
                  <Input type="email" placeholder={t('ENTER_YOUR_EMAIL')} {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="country"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-sm">{t('COUNTRY')}</FormLabel>
                <FormControl>
                  <CountrySelect
                    value={field.value}
                    onChange={field.onChange}
                    placeholder={t('SELECT_COUNTRY') || 'Select your country'}
                    className="rounded-md"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <Captcha
          type="reCaptcha"
          siteKey={configurationSettings.googleCaptchaSiteKey}
          theme={captchaTheme}
          onVerify={handleCaptchaVerify}
          onExpired={handleCaptchaExpired}
          setResetFn={setResetCaptcha}
          size="normal"
        />

        <div className="flex justify-between items-center">
          <FormCheckbox
            control={form.control}
            name="terms"
            label={(() => {
              const termsText = t('TERMS_OF_SERVICE');
              const privacyText = t('PRIVACY_POLICY');

              const termsLink = (
                <span className="text-primary underline hover:text-primary-600">
                  <a href="https://app.selise.ai/en/term-of-conditions/" target="_blank" rel="noreferrer">{termsText}</a>
                </span>
              );
              const privacyLink = (
                <span className="text-primary underline hover:text-primary-600">
                  <a href="https://app.selise.ai/en/privacy-policy/" target="_blank" rel="noreferrer">{privacyText}</a>
                </span>
              );

              const translationText = t('TERMS_AND_PRIVACY', {
                termsofuse: termsText,
                privacypolicy: privacyText,
              });

              // Escape special regex characters
              const escapeRegex = (str: string) => str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

              // Replace translated terms with JSX links
              const parts = translationText.split(new RegExp(`(${escapeRegex(termsText)}|${escapeRegex(privacyText)})`, 'g'));
              return (
                <>
                  {parts.map((part, index) => {
                    if (part === termsText) {
                      return <span key={`terms-${index}`}>{termsLink}</span>;
                    }
                    if (part === privacyText) {
                      return <span key={`privacy-${index}`}>{privacyLink}</span>;
                    }
                    return <span key={`text-${index}`}>{part}</span>;
                  })}
                </>
              );
            })()}
            labelClassName="text-medium-emphasis font-normal leading-5"
          />
        </div>

          <Button
            className={`w-full bg-primary-600 hover:bg-primary-700 ${theme === 'dark' ? 'text-neutral-800' : 'text-neutral-50'}`}
            type="submit"
            disabled={isSubmitting || !captchaToken || !isValid}
            loading={isPending}
          >
            {t('SIGN_UP')}
          </Button>
        </form>
      </Form>
    </div>
  );
};
